/**
 * 侧边导航组件
 * 
 * 修改说明：
 * 1. 使用 message.useMessage() 钩子替代直接调用 message 函数
 * 2. 添加 contextHolder 确保消息正确渲染
 * 3. 使用 messageApi 代替全局 message 对象
 * 4. 同步功能已移动到 SettingsDrawer 组件中
 */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux'; // 引入 Redux 钩子
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './SideNav.css';
import { defaultNavItems as configDefaultNavItems } from '../config/defaultData';
import AddCategoryPopup from './popup/AddCategoryPopup';
import FontIcon from './common/FontIcon';
import LoginModal from './popup/LoginModal';
import RegisterModal from './popup/RegisterModal';
import ResetPasswordModal from './popup/ResetPasswordModal';
import { Modal } from 'antd';
import { message } from 'antd';
// 引入 Redux action
import { 
  addNavItemAsync, 
  updateNavItemAsync, 
  deleteNavItemAsync, 
  reorderNavItemsAsync 
} from '../redux/slices/navSlice';
import { logoutUser } from '../redux/slices/authSlice';
import logger from '../utils/logger';
import { GlobalMessageApiContext } from '../App';
import trackingManager from '../services/trackingManager';
import languageManager from '../utils/languageManager';

// 右键菜单组件
const ContextMenu = ({ position, onEdit, onDelete, onClose }) => {
  const menuRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  return (
    <div 
      className="context-menu"
      style={{ top: position.y, left: position.x }}
      ref={menuRef}
    >
      <div className="menu-item" onClick={onEdit}>
        <FontIcon 
          name="edit" 
          style={{ width: '20px', marginRight: '8px' }}
          size="14px"
        />
        <span>{languageManager.t('sideNav.editCategory')}</span>
      </div>
      <div className="menu-item" onClick={onDelete}>
        <FontIcon 
          name="trash-alt" 
          style={{ width: '20px', marginRight: '8px' }}
          size="14px"
        />
        <span>{languageManager.t('sideNav.delete')}</span>
      </div>
    </div>
  );
};

// 可排序的菜单项组件
const SortableNavItem = ({ item, isActive, onItemClick, onContextMenu }) => {
  // 引用当前元素，用于样式控制
  const elementRef = useRef(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    transition: {
      duration: 200, // 保持动画持续时间适中
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)' // 使用自定义缓动函数，优化动画曲线
    },
    disabled: item.disableDrag, // 如果项目有disableDrag属性，则禁用拖动
    onDragStart: () => {
      // 如果禁用了拖动，则不执行拖动逻辑
      if (item.disableDrag) {
        return;
      }
      
      // 记录开始拖拽的时间和状态
      window.dragStartTime = Date.now();
      window.isDragging = true;
      window.skipMainContainerAnimation = true;
      
      // 拖拽开始时，添加标记到 body
      document.body.setAttribute('data-dragging', 'true');
      
      // 保存当前主容器的位置，以便在拖拽结束后恢复
      if (document.querySelector('.pages-container')) {
        const containerStyle = getComputedStyle(document.querySelector('.pages-container'));
        const transform = containerStyle.transform;
        window.lastMainContainerTransform = transform;
      }
      
      // 立即移除所有过渡效果
      if (elementRef.current) {
        elementRef.current.style.transition = 'none';
      }
    },
    onDragEnd: () => {
      // 拖拽结束时的处理由 handleDragEnd 函数统一处理
      window.isDragging = false;
    }
  });

  useEffect(() => {
    // 组件挂载时设置引用
    if (elementRef.current) {
      setNodeRef(elementRef.current);
    }
    
    // 组件卸载时确保清理拖拽状态
    return () => {
      // 如果这个项目正在被拖拽，确保在卸载时清理拖拽状态
      if (isDragging) {
        document.body.removeAttribute('data-dragging');
        document.body.classList.remove('animation-freeze');
        window.isDragging = false;
        window.skipMainContainerAnimation = false;
      }
    };
  }, [isDragging, setNodeRef]);

  // 使用更稳定的样式计算方法，防止抖动
  const style = useMemo(() => {
    // 如果是不可拖动的项目，则使用简单的样式
    if (item.disableDrag) {
      return {
      margin: "1px 0",
      boxSizing: "border-box",
      width: "100%",
      opacity: 1,
        position: 'relative',
        zIndex: 1
      };
    }
    
    // 如果不是拖拽状态，使用精确计算的transform和transition
    return {
      margin: "1px 0",
      boxSizing: "border-box",
      width: "100%",
      opacity: 1,
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? 'none' : transition,
      opacity: 1,
      zIndex: isDragging ? 999 : 1,
      position: 'relative',
      // 添加硬件加速提高动画性能
      willChange: isDragging ? 'transform' : 'auto'
    };
  }, [transform, transition, isDragging, item.disableDrag, isActive]);

  // 对于不可拖动的项目，移除拖拽相关的属性和监听器
  const dragProps = item.disableDrag ? {} : {
    ...attributes,
    ...listeners
  };

  return (
    <div
      ref={(el) => {
        elementRef.current = el;
        setNodeRef(el);
      }}
      style={style}
      className={`nav-item ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${item.disableDrag ? 'disable-drag' : ''}`}
      onClick={() => onItemClick(item)}
      onContextMenu={(e) => onContextMenu(e, item.id)}
      {...dragProps}
    >
      <div className="nav-item-content">
        <FontIcon name={item.icon.replace('fas fa-', '')} />
        <div className="nav-item-text">{item.text}</div>
      </div>
      {!item.disableDrag && (
        <div className="nav-item-drag-handle">
          <FontIcon name="grip-lines" />
        </div>
      )}
    </div>
  );
};

const SideNav = ({ activeItem, onItemClick, onSettingsClick }) => {
  // 使用 Redux
  const dispatch = useDispatch();
  const { navItems } = useSelector(state => state.nav);
  // 直接从 Redux 获取认证状态
  const { isLoggedIn, user } = useSelector(state => state.auth);
  // 获取侧栏自动隐藏设置
  const { sideNavAutoHide } = useSelector(state => state.settings);
  
  const messageApi = useContext(GlobalMessageApiContext);
  
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  // 自动隐藏相关状态
  const [isHidden, setIsHidden] = useState(false);
  const hideTimerRef = useRef(null);
  const [languageKey, setLanguageKey] = useState(0); // 用于触发重新渲染的语言键
  
  // 认证状态和模态框状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  
  // 上下文菜单状态
  const [contextMenu, setContextMenu] = useState({
    show: false,
    position: { x: 0, y: 0 },
    itemId: null
  });
  
  // 当前活动项状态
  const [currentActiveItem, setActiveItem] = useState(activeItem);
  
  // 智能加号按钮定位状态
  const [isMenuOverflowing, setIsMenuOverflowing] = useState(false);
  const navItemsRef = useRef(null);

  // 监听activeItem prop的变化
  useEffect(() => {
    setActiveItem(activeItem);
  }, [activeItem]);

  // 智能检测菜单项是否溢出，决定加号按钮的定位方式
  useEffect(() => {
    const checkMenuOverflow = () => {
      if (navItemsRef.current) {
        const container = navItemsRef.current;
        const isOverflowing = container.scrollHeight > container.clientHeight;
        setIsMenuOverflowing(isOverflowing);
      }
    };

    // 初始检测
    checkMenuOverflow();

    // 监听窗口大小变化
    window.addEventListener('resize', checkMenuOverflow);
    
    // 监听菜单项数量变化
    const observer = new ResizeObserver(checkMenuOverflow);
    if (navItemsRef.current) {
      observer.observe(navItemsRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkMenuOverflow);
      observer.disconnect();
    };
  }, [navItems]); // 依赖 navItems，当菜单项变化时重新检测

  // 自动隐藏逻辑
  useEffect(() => {
    // 如果自动隐藏功能关闭，确保侧边栏显示
    if (!sideNavAutoHide) {
      setIsHidden(false);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      return;
    }

    const startHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => {
        setIsHidden(true);
      }, 5000); // 增加到5秒后自动隐藏
    };

    const resetHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      setIsHidden(false);
      startHideTimer();
    };

    const handleMouseMove = (e) => {
      // 只在左侧120px区域内的鼠标移动才触发显示
      if (e.clientX <= 120) {
        resetHideTimer();
      }
    };

    // 启动初始定时器
    startHideTimer();

    // 监听鼠标移动（全局）
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [sideNavAutoHide]);

  // 设置传感器
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 处理菜单项点击
  const handleItemClick = (item) => {
    // 先更新当前选中项
    setActiveItem(item.id);
    // 然后再调用父组件的回调
    if (onItemClick) {
      onItemClick(item);
    }
  };

  // 处理右键点击
  const handleContextMenu = (e, itemId) => {
    e.preventDefault();
    
    // 查找当前项目
    const currentItem = navItems.find(item => item.id === itemId);
    
    // 如果是首页或标记为不可拖动的项目，则不显示右键菜单
    if (currentItem && (currentItem.id === 'home' || currentItem.disableDrag)) {
      return;
    }
    
    setContextMenu({
      show: true,
      position: { x: 61, y: e.clientY }, // 设置为侧边栏宽度(55px) + 间距(6px)
      itemId
    });
  };

  // 处理关闭右键菜单
  const handleCloseContextMenu = () => {
    setContextMenu({ show: false, position: { x: 0, y: 0 }, itemId: null });
  };

  // 处理编辑菜单项
  const handleEditItem = () => {
    const item = navItems.find(item => item.id === contextMenu.itemId);
    if (item) {
      setEditItem(item);
      setShowAddPopup(true);
    }
    handleCloseContextMenu();
  };

  // 处理删除菜单项
  const handleDeleteItem = () => {
    if (contextMenu.itemId) {
      const item = navItems.find(item => item.id === contextMenu.itemId);
      if (item) {
        // 上报分类删除埋点
        trackingManager.reportCategoryAction('DELETE', item.text);
      }
      // 使用 Redux action 删除导航项
      dispatch(deleteNavItemAsync(contextMenu.itemId));
    }
    handleCloseContextMenu();
  };

  // 处理拖拽结束
  const handleDragEnd = (event) => {
    // 如果拖动被取消，则不做任何改变
    if (!event.over) {
      return;
    }

    // 找到对应的ID
    const activeId = event.active.id;
    const overId = event.over.id;

    // 如果拖动到相同位置，则不做修改
    if (activeId === overId) {
      return;
    }

    // 找到元素在数组中的位置
    const oldIndex = navItems.findIndex(item => item.id === activeId);
    const newIndex = navItems.findIndex(item => item.id === overId);
    
    // 上报分类拖拽埋点
    const activeItem = navItems.find(item => item.id === activeId);
    if (activeItem) {
      trackingManager.reportUserAction('drag_category', activeItem.text, `从位置${oldIndex}拖拽到位置${newIndex}`);
    }
    
    // 使用 Redux action 重新排序导航项
    dispatch(reorderNavItemsAsync({ sourceIndex: oldIndex, targetIndex: newIndex }));
  };

  // 处理保存新分类
  const handleSaveCategory = (categoryData) => {
    // 处理编辑现有分类
    if (editItem) {
      // 上报分类编辑埋点
      trackingManager.reportCategoryAction('EDIT', categoryData.name);
      
      // 使用 Redux action 更新导航项
      dispatch(updateNavItemAsync({
        id: editItem.id,
        updates: {
          text: categoryData.name,
          icon: categoryData.icon || editItem.icon
        }
      }));
    } else {
      // 上报分类添加埋点
      trackingManager.reportCategoryAction('ADD', categoryData.name);
      
      // 添加新分类
      const newId = `custom_${Date.now()}`; // 使用时间戳创建唯一ID
      
      const newItem = {
        id: newId,
        text: categoryData.name,
        icon: categoryData.icon || 'fas fa-folder',
        index: navItems.length,
        disableDrag: false,
        route: `/category/${newId}`
      };
      
      // 使用 Redux action 添加导航项
      dispatch(addNavItemAsync(newItem));
      
      // 设置新项目为当前选中项
      setActiveItem(newId);
      
      // 调用父组件的回调
      if (onItemClick) {
        onItemClick(newItem);
      } else {
        logger.warn('[SideNav] onItemClick回调不存在，无法切换到新分类');
      }
    }
    
    setShowAddPopup(false);
    setEditItem(null);
  };

  // 处理关闭弹窗
  const handleClosePopup = () => {
    setShowAddPopup(false);
    setEditItem(null);
  };

  // 处理登录区域点击
  const handleLoginClick = () => {
    if (isLoggedIn) {
      // 如果已登录，显示退出登录确认对话框
      setLogoutModalVisible(true);
    } else {
      // 上报用户登录埋点
      trackingManager.reportUserAuth('LOGIN');
      // 显示登录模态框
      setShowLoginModal(true);
    }
  };
  
  // 处理确认退出登录
  const handleLogoutConfirm = () => {
    // 使用 Redux action 登出
    dispatch(logoutUser());
    
    // 关闭登出确认对话框
    setLogoutModalVisible(false);
    
    // 强制触发一个全局事件，通知其他组件状态已更改
    window.dispatchEvent(new Event('authStatusChanged'));
  };
  
  // 处理切换到注册页面
  const handleShowRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };
  
  // 处理切换到登录页面
  const handleShowLogin = () => {
    setShowRegisterModal(false);
    setShowResetPasswordModal(false);
    setShowLoginModal(true);
  };
  
  // 处理切换到重置密码页面
  const handleShowResetPassword = () => {
    setShowLoginModal(false);
    setShowResetPasswordModal(true);
  };
  
  // 处理关闭所有认证相关模态框
  const handleCloseAuthModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setShowResetPasswordModal(false);
    
    // 如果用户已登录，显示登录成功提示
    if (isLoggedIn) {
      messageApi.success(languageManager.t('sideNav.loginSuccess'));
    }
  };

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = (language) => {
      setLanguageKey(prev => prev + 1);
    };
    
    const unsubscribe = languageManager.addListener(handleLanguageChange);
    
    // 也监听 window 事件
    const handleWindowLanguageChange = (event) => {
      const { language } = event.detail || {};
      if (language) {
        setLanguageKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('languageChanged', handleWindowLanguageChange);
    
    return () => {
      unsubscribe();
      window.removeEventListener('languageChanged', handleWindowLanguageChange);
    };
  }, []);

  // 监听来自其他组件的登录模态框显示请求
  useEffect(() => {
    const handleShowLoginModal = () => {
      setShowLoginModal(true);
    };
    
    const handleAuthStatusChanged = () => {
      // Redux 会自动处理状态更新，无需手动触发
    };
    
    // 添加全局事件监听
    window.addEventListener('showLoginModal', handleShowLoginModal);
    window.addEventListener('authStatusChanged', handleAuthStatusChanged);
    
    // 清理函数
    return () => {
      window.removeEventListener('showLoginModal', handleShowLoginModal);
      window.removeEventListener('authStatusChanged', handleAuthStatusChanged);
    };
  }, [isLoggedIn, user, navItems]);
  
  // 监听navItems变化
  useEffect(() => {
    // 空实现，仅用于监听变化
  }, [navItems, currentActiveItem]);
  
  return (
    <>
      <div 
        className={`side-nav ${isHidden ? 'hidden' : ''}`}
        onMouseEnter={() => {
          if (sideNavAutoHide) {
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
            }
            setIsHidden(false);
          }
        }}
        onMouseLeave={() => {
          if (sideNavAutoHide) {
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
            }
            // 鼠标离开侧边栏时，延迟1秒后隐藏
            hideTimerRef.current = setTimeout(() => {
              setIsHidden(true);
            }, 1000);
          }
        }}
      >
        <div className="logo-container" onClick={handleLoginClick}>
          <img
          className="logo"
          src={require('../assets/logo192.png')}
            alt="logo" 
          />
          <div 
            className="logo-text"
            title={isLoggedIn ? (user?.email || '') : languageManager.t('sideNav.clickToLogin')}
          >
            {isLoggedIn ? (user?.nickname || user?.email || languageManager.t('sideNav.user')) : languageManager.t('sideNav.login')}
          </div>
        </div>
        
        <div className="dnd-container">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragEnd}
            autoScroll={false}
            modifiers={[]}
            measuring={{
              droppable: {
                strategy: 'always'
              }
            }}
          >
            <div 
              ref={navItemsRef}
              className={`nav-items ${isMenuOverflowing ? 'overflowing' : 'not-overflowing'}`}
              onWheel={(e) => {
                // 阻止滚动事件透传到外部
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // 阻止触摸滚动事件透传到外部
                e.stopPropagation();
              }}
            >
              <SortableContext
                items={Array.isArray(navItems) ? navItems.filter(item => item && typeof item === 'object' && item.id).map(item => item.id) : []}
                strategy={verticalListSortingStrategy}
              >
                {Array.isArray(navItems) ? navItems
                  .filter(item => item && typeof item === 'object' && item.id)
                  .map(item => {
                    const isActive = activeItem === item.id;
                    return (
                  <SortableNavItem
                    key={item.id}
                    item={item}
                        isActive={isActive}
                    onItemClick={handleItemClick}
                    onContextMenu={handleContextMenu}
                  />
                    );
                  }) : null}
              </SortableContext>
              {/* 智能加号按钮：菜单项少时跟随，菜单项多时固定 */}
              <div 
                className="add-button" 
                onClick={() => setShowAddPopup(true)}
                onMouseEnter={(e) => {
                  const plusIcon = e.currentTarget.querySelector('.plus-icon');
                  if (plusIcon) {
                    plusIcon.classList.remove('rotate-counterclockwise');
                    plusIcon.classList.add('rotate-clockwise');
                  }
                }}
                onMouseLeave={(e) => {
                  const plusIcon = e.currentTarget.querySelector('.plus-icon');
                  if (plusIcon) {
                    plusIcon.classList.remove('rotate-clockwise');
                    plusIcon.classList.add('rotate-counterclockwise');
                  }
                }}
              >
                <FontIcon name="plus" size="16px" className="plus-icon" />
              </div>
            </div>
          </DndContext>
        </div>
        
        <div 
          className="settings" 
          onClick={onSettingsClick}
          onMouseEnter={(e) => {
            const cogIcon = e.currentTarget.querySelector('.cog-icon');
            if (cogIcon) {
              cogIcon.classList.remove('rotate-counterclockwise');
              cogIcon.classList.add('rotate-clockwise');
            }
          }}
          onMouseLeave={(e) => {
            const cogIcon = e.currentTarget.querySelector('.cog-icon');
            if (cogIcon) {
              cogIcon.classList.remove('rotate-clockwise');
              cogIcon.classList.add('rotate-counterclockwise');
            }
          }}
        >
          <FontIcon name="cog" className="cog-icon" />
        </div>
      </div>
      
      {contextMenu.show && (
        <ContextMenu 
          position={contextMenu.position}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          onClose={handleCloseContextMenu}
        />
      )}
      
      {showAddPopup && (
        <AddCategoryPopup 
          onClose={handleClosePopup} 
          onSave={handleSaveCategory}
          editItem={editItem}
        />
      )}
      
      {/* 登出确认对话框 */}
      <Modal
        title={languageManager.t('sideNav.confirmLogout')}
        open={logoutModalVisible}
        onOk={handleLogoutConfirm}
        onCancel={() => setLogoutModalVisible(false)}
        okText={languageManager.t('common.confirm')}
        cancelText={languageManager.t('common.cancel')}
        zIndex={2100}
        styles={{
          mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' }
        }}
        style={{ top: '30%' }}
      >
        <p>{languageManager.t('sideNav.confirmLogoutText')}</p>
      </Modal>
      
      {/* 认证相关模态框 */}
      <LoginModal 
        visible={showLoginModal} 
        onClose={handleCloseAuthModals}
        onShowRegister={handleShowRegister}
        onShowResetPassword={handleShowResetPassword}
      />
      
      <RegisterModal 
        visible={showRegisterModal} 
        onClose={handleCloseAuthModals}
        onShowLogin={handleShowLogin}
      />
      
      <ResetPasswordModal 
        visible={showResetPasswordModal} 
        onClose={handleCloseAuthModals}
        onShowLogin={handleShowLogin}
      />
    </>
  );
};

export default SideNav;